<template>
  <div
    class="base-color"
    :class="color"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
/**
 * Transparent color wrapper that cascades text color to children
 *
 * Uses CSS custom properties to set text color for child components
 * without adding extra DOM elements (display: contents). All brand
 * colors use shade 400 for consistent intensity.
 *
 * @example Colored text and icons
 * ```vue
 * <BaseColor color="blue">
 *   <BaseIcon id="info-circle" inline />
 *   <BaseText inline>This text and icon are blue</BaseText>
 * </BaseColor>
 * ```
 *
 * @example Muted secondary text
 * ```vue
 * <BaseColor color="muted">
 *   <BaseText size="sm">
 *     Secondary information in neutral gray
 *   </BaseText>
 * </BaseColor>
 * ```
 *
 * @example Error message styling
 * ```vue
 * <BaseColor color="red">
 *   <BaseIcon id="warning" inline />
 *   <span>Error: Invalid input</span>
 * </BaseColor>
 * ```
 */
export interface BaseColorProps {
  /**
   * Text color from design system
   * - Brand colors: Uses shade 400 for medium intensity
   * - white: Light color for dark backgrounds
   * - muted: Neutral 500 for secondary text
   * @default null (uses default dark text color)
   */
  color?:
    | 'white'
    | 'blue'
    | 'red'
    | 'amber'
    | 'green'
    | 'teal'
    | 'purple'
    | 'pink'
    | 'gray'
    | 'yellow'
    | 'muted'
    | null;
}

withDefaults(defineProps<BaseColorProps>(), {
  color: null,
});
</script>

<style>
.base-color {
  display: contents;
  --text-color: var(--color-dark);
  color: var(--text-color);
  &.white {
    --text-color: var(--color-light);
  }
  &.blue {
    --text-color: var(--color-blue-400);
  }
  &.green {
    --text-color: var(--color-green-400);
  }
  &.red {
    --text-color: var(--color-red-400);
  }
  &.amber {
    --text-color: var(--color-amber-400);
  }
  &.gray {
    --text-color: var(--color-gray-400);
  }
  &.yellow {
    --text-color: var(--color-yellow-400);
  }
  &.purple {
    --text-color: var(--color-purple-400);
  }
  &.pink {
    --text-color: var(--color-pink-400);
  }
  &.teal {
    --text-color: var(--color-teal-400);
  }
  &.muted {
    --text-color: var(--color-neutral-500);
  }
}
</style>
